// ?===============================
// ?=====  3rd party Imports  =====
// ?===============================
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'gatsby-link'
import { Flex, Box, NavLink, Link as ExternalLink, Heading } from '@theme-ui/components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPhone } from '@fortawesome/free-solid-svg-icons'
import Fade from 'react-reveal/Fade'
import { nanoid } from 'nanoid'
import slugify from 'slugify'

// ?===================================
// ?===  Local Components Imports  ====
// ?===================================

import NavLinkSelector from '../NavComponents/NavLinkSelector'
import DropdownNavItem from '../dropdownComponents/DropdownNavItemV1Click'
import CloudinaryImage from '../../../../General/CloudinaryImage'
import LocationSwitcher from '../../../../LocationSwitchers/v1/LocationSwitcher'
import SocialIcons from '../../../../General/SocialIcons/SocialIconsV1'

// ?===================================
// ?====  Local Function Imports  =====
// ?===================================

import combineSocialLinks from '../../../../../helpers/combineSocialLinks'

// ?===================================
// ?==========  Component  ============
// ?===================================

export default function Index(props) {
  // ?==========================
  // ?====== Destructing  ======
  // ?==========================
  const {
    businessData,
    configData,
    isMultiLocationSite,
    links,
    locationIndex,
    locations,
    pageObjectKey
    // navLinks, pageComponents, customPageName, gonationID
  } = props
  const {
    avatar: { imageBaseUrl, imagePrefix },
    city,
    slug,
    links: socialLinks,
    phone
  } = businessData

  const { variantName, options } = configData.navigation

  const { containerStyles, socialLinksHidden, phoneHidden, numberDesktopNavLinks, linkedin, pinterest } = options

  // ?===========================
  // ?===== Component State =====
  // ?===========================
  const [navOpen, toggleNav] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  // ?==============================
  // ?=====  Variable Creation  ====
  // ?==============================

  const variant = variantName ? variantName : 'navigationV4'
  const styles = containerStyles ? JSON.parse(containerStyles) : {}
  const navRef = useRef()
  navRef.current = scrolled
  const configuredSocialLinks =
    linkedin || pinterest
      ? combineSocialLinks(socialLinks, linkedin?.[locationIndex], pinterest?.[locationIndex])
      : socialLinks

  // ?=============================================
  // ?==============   Functions   ================
  // ?=============================================

  const handleScroll = useCallback(() => {
    const show = window.scrollY > 70
    if (navRef.current !== show) {
      setScrolled(show)
    }
  })

  // ?============================================
  // ?===========   ON/OFF MOUNTING   ============
  // ?============================================

  useEffect(() => {
    document.addEventListener('scroll', handleScroll)
    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  // ?=========================
  // ?===== JSX rendering =====
  // ?=========================

  return (
    <Box sx={styles}>
      <Fade top cascade>
        <Flex
          variant={`${variant}.${scrolled || navOpen ? 'containerScrolled' : 'container'}`}
          className={`${scrolled || navOpen ? 'containerScrolled' : 'container'}`}
          as="nav"
        >
          <Box variant={`${variant}.smallNavMenuLeft`} className="smallNavMenuLeft smallNavMenu">
            <Fade top cascade>
              {links
                .slice(0, numberDesktopNavLinks?.[locationIndex] ? numberDesktopNavLinks[locationIndex] / 2 : 2)
                .map((page, index) => {
                  const { ctaName, ctaLink, className, dropdownLinks } = page

                  if (dropdownLinks) {
                    return (
                      <DropdownNavItem
                        navLinks={page}
                        variantName={variant}
                        isMultiLocationSite={isMultiLocationSite}
                        city={city}
                        key={nanoid()}
                        index={`left${index}`}
                      />
                    )
                  } else {
                    return (
                      <NavLinkSelector
                        key={nanoid()}
                        ctaName={ctaName}
                        className={className}
                        variant={variant}
                        ctaLink={ctaLink}
                        isMultiLocationSite={isMultiLocationSite}
                        city={city}
                        page={page}
                        pageObjectKey={pageObjectKey}
                      />
                    )
                  }
                })}
            </Fade>
          </Box>

          {/* This logo locations contains the logo and location switcher.  */}
          <Box variant={`${variant}.logoLocationContainer`} className="logoLocationContainer">
            <Flex
              variant={`${variant}.${scrolled || navOpen ? 'logoScrolled' : 'logo'}`}
              className={`${scrolled || navOpen ? 'logoScrolled' : 'logo'}`}
            >
              <Link to={isMultiLocationSite ? `/${slugify(city, { lower: true })}` : `/`}>
                <CloudinaryImage imageBaseUrl={imageBaseUrl} imagePrefix={imagePrefix} />
              </Link>
            </Flex>

            {isMultiLocationSite && (
              <Flex variant={`${variant}.locationSwitcher`} className="locationSwitcher">
                <LocationSwitcher businessData={businessData} locations={locations} />
              </Flex>
            )}
          </Box>

          {/* // this is the small nav on desktop only used to display the main nav items */}
          <Box variant={`${variant}.smallNavMenuRight`} className="smallNavMenuRight smallNavMenu">
            <Fade top cascade>
              {links
                .slice(
                  numberDesktopNavLinks?.[locationIndex] ? numberDesktopNavLinks?.[locationIndex] / 2 : 2,
                  numberDesktopNavLinks?.[locationIndex] ? numberDesktopNavLinks[locationIndex] : 4
                )
                .map((page, index) => {
                  const { ctaName, ctaLink, className, dropdownLinks } = page

                  if (dropdownLinks) {
                    return (
                      <DropdownNavItem
                        navLinks={page}
                        variantName={variant}
                        isMultiLocationSite={isMultiLocationSite}
                        city={city}
                        key={nanoid()}
                        index={`right${index}`}
                      />
                    )
                  } else {
                    return (
                      <NavLinkSelector
                        key={nanoid()}
                        ctaName={ctaName}
                        className={className}
                        variant={variant}
                        ctaLink={ctaLink}
                        isMultiLocationSite={isMultiLocationSite}
                        city={city}
                        page={page}
                      />
                    )
                  }
                })}
            </Fade>
          </Box>

          <Flex
            variant={`${variant}.${!navOpen ? 'hamburger' : 'hamburgerOpen'}`}
            className={`${!navOpen ? 'hamburger' : 'hamburgerOpen'}`}
            onClick={() => {
              toggleNav(!navOpen)
            }}
          >
            <div></div>
            {!navOpen ? <div></div> : ''}
            <div></div>
          </Flex>

          <Flex
            variant={`${variant}.${!navOpen ? 'navMenu' : 'navMenuOpen'}`}
            className={`${!navOpen ? 'navMenu' : 'navMenuOpen'}`}
          >
            <NavLink
              as="li"
              variant={`${variant}.navMenuLogo`}
              className="navMenuLogo"
              to={isMultiLocationSite ? `/${slugify(city, { lower: true })}` : `/`}
            >
              <CloudinaryImage imageBaseUrl={imageBaseUrl} imagePrefix={imagePrefix} />
            </NavLink>

            {links.map((page, index) => {
              const { ctaName, ctaLink, className, dropdownLinks } = page

              if (dropdownLinks) {
                return (
                  <DropdownNavItem
                    navLinks={page}
                    variantName={variant}
                    isMultiLocationSite={isMultiLocationSite}
                    city={city}
                    key={nanoid()}
                    index={index}
                  />
                )
              } else {
                return (
                  <NavLinkSelector
                    key={nanoid()}
                    ctaName={ctaName}
                    className={className}
                    variant={variant}
                    ctaLink={ctaLink}
                    isMultiLocationSite={isMultiLocationSite}
                    city={city}
                    page={page}
                  />
                )
              }
            })}

            <hr className="seperatorLine" />

            <Box variant={`${variant}.phoneSocialContainer`} className="phoneSocialContainer">
              {phoneHidden ? (
                ''
              ) : (
                <Box variant={`${variant}.phoneContainer`} as="li" className="phoneContainer">
                  <Heading variant={`${variant}.smallNavHeading`} className="smallNavHeading socialHeading">
                    Get In Touch.
                  </Heading>
                  <ExternalLink href={`tel:${phone}`} target="_blank" rel="noreferrer">
                    <FontAwesomeIcon icon={faPhone} /> {phone}
                  </ExternalLink>
                </Box>
              )}

              {socialLinksHidden ? (
                ''
              ) : (
                <Flex variant={`${variant}.socialIconsContainer`} as="li" className="socialIconsContainer">
                  <Heading variant={`${variant}.smallNavHeading`} className="smallNavHeading socialHeading">
                    Follow Us
                  </Heading>
                  <SocialIcons links={configuredSocialLinks} slug={slug} />
                </Flex>
              )}
            </Box>
          </Flex>

          <Flex
            variant={`${variant}.${!navOpen ? 'navBlock' : 'navBlockOpen'}`}
            className={`${!navOpen ? 'navBlock' : 'navBlockOpen'}`}
            onClick={() => {
              toggleNav(!navOpen)
            }}
          />
        </Flex>
      </Fade>
    </Box>
  )
}
